/**
 * Derives the pieces of the questionnaire page UI from a
 * {@link QuestionnaireReviewResponse} envelope: the PDF path used by
 * {@link useFetchDocument}, the document name shown next to it, and the
 * structured questionnaire payload.
 *
 * Replaces the legacy `PharmaQuestionsModel`-driven hook. Outputs are
 * memoized/derived directly from the response so the parent owns
 * fetching while this hook owns parsing.
 *
 * @module pharmaQuestionaire/hooks/useQuestionnaireState
 */
import { useEffect, useMemo, useState } from "react";
import { PharmaBranchedQuestionnaire } from "../../../data-model/cmmMedicalNeccessityQuestionnaire";
import { QuestionnaireReviewResponse } from "../../../data-model/questionnaireReview";

/**
 * Extracts the trailing file-name segment from a storage path or URL.
 * Returns an empty string when the input is empty or has no segment.
 *
 * @param path - Full storage path (e.g. `"folder/sub/file.pdf"`) or URL
 * @returns The trailing segment, or `""` when unavailable
 */
const extractFileName = (path: string | null | undefined): string => {
  if (!path) return "";
  const trimmed = path.split("?")[0];
  const segments = trimmed.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? "";
};

/** Shape returned to consumers of {@link useQuestionnaireState}. */
interface UseQuestionnaireStateReturn {
  /** Parsed questionnaire payload (sections + questions), or `null`. */
  questionnaire: PharmaBranchedQuestionnaire | null;
  /** PDF storage path used to drive {@link useFetchDocument}. */
  pdfUrl: string | null;
  /** Display name for the document; defaults to the basename of `pdfUrl`. */
  fileNames: string[];
  /** Manual override for {@link fileNames} (e.g. after a successful merge). */
  setFileNames: (urls: string[]) => void;
  /** Identifier of the review record, or `""` when not yet loaded. */
  identifier: string;
  /** Drug name for header / fallback labels. */
  drugName: string;
  /** Initial questionnaire payload, or `null`. */
  initialQuestionnaire: PharmaBranchedQuestionnaire | null;
}

/**
 * Parses a {@link QuestionnaireReviewResponse} into the slices the
 * questionnaire page needs.
 *
 * @param review - Latest response envelope returned by the review API,
 *                 or `null` / `undefined` while it is still loading
 * @returns      Parsed questionnaire, PDF path, file names, and metadata
 */
export const useQuestionnaireState = (
  review: QuestionnaireReviewResponse | null | undefined,
): UseQuestionnaireStateReturn => {
  const details = review?.details;

  const derivedPdfUrl = details?.notes_path ?? null;
  const derivedFileName = useMemo(
    () => extractFileName(details?.notes_path) || (details?.drug_name ?? ""),
    [details?.notes_path, details?.drug_name],
  );

  const [fileNames, setFileNames] = useState<string[]>([]);

  useEffect(() => {
    setFileNames(derivedFileName ? [derivedFileName] : []);
  }, [derivedFileName]);

  return {
    questionnaire: details?.questionnaire ?? null,
    pdfUrl: derivedPdfUrl,
    fileNames,
    setFileNames,
    identifier: details?.identifier ?? "",
    drugName: details?.drug_name ?? "",
    initialQuestionnaire: details?.questionnaire ?? null,
  };
};
